import type { ReactNode } from 'react';
import { useEffect, useRef } from 'react';
import { Button } from './Button';

interface ConfirmDialogProps {
  isOpen: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  /** Name the specific thing, e.g. "Reverse the K50.00 sent on 3 Oct?" */
  title: string;
  children?: ReactNode;
  confirmLabel: string;
  /** Red-outlined confirm button, for deleting and reversing. */
  destructive?: boolean;
  isLoading?: boolean;
}

/** A centred confirmation. Cancel has initial focus so Enter never confirms by accident. */
export const ConfirmDialog = ({
  isOpen,
  onCancel,
  onConfirm,
  title,
  children,
  confirmLabel,
  destructive = false,
  isLoading = false,
}: ConfirmDialogProps) => {
  const dialogRef = useRef<HTMLDivElement>(null);
  // Callers pass inline arrows; keep the latest in a ref and depend on isOpen alone (see Sheet.tsx).
  const onCancelRef = useRef(onCancel);
  useEffect(() => {
    onCancelRef.current = onCancel;
  });

  useEffect(() => {
    if (!isOpen) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    // Focus the Cancel button (first button in the dialog) so Enter never confirms by accident.
    dialogRef.current?.querySelector<HTMLButtonElement>('button')?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancelRef.current();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      previouslyFocused?.focus?.();
    };
  }, [isOpen]);

  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-ink/40" onClick={onCancel} aria-hidden />
      <div
        ref={dialogRef}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="confirm-title"
        className="relative w-full max-w-md rounded-card bg-surface p-6 shadow-xl"
      >
        <h2 id="confirm-title" className="text-section font-semibold text-ink">
          {title}
        </h2>
        {children && <div className="mt-2 text-body text-muted">{children}</div>}
        <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
          <Button variant={destructive ? 'danger' : 'primary'} onClick={onConfirm} isLoading={isLoading}>
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
};
